import { integrationsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { isoDateDaysAgo, todayIsoDate } from '~/utils/date';

import type {
  CategoriesListRequest,
  CategoriesListResponse,
  DefaultGroupingConfigResponse,
  ItemGroupChildrenRequest,
  ItemGroupChildrenResponse,
  ItemGroupsListRequest,
  ItemGroupsListResponse,
  ProductColumnsResponse,
  ProductDetailResponse,
  ProductFiltersRequest,
  ProductFiltersResponse,
  ProductRevisionsRequest,
  ProductRevisionsResponse,
  ProductSearchFieldsResponse,
  ProductsListRequest,
  ProductsListResponse,
  SyncCapabilitiesResponse,
  SyncProductDeleteRequest,
  SyncProductRequest,
  SyncProductResponse,
} from '../types/products-catalog.types';

/**
 * Ky request options that override the `X-Subsidiary-ID` header for a single
 * call. Lets the Products page scope its requests to a picked subsidiary
 * without touching the global active tenant; the subsidiary interceptor leaves
 * an explicitly-set header untouched. Returns `{}` when `subsidiaryId` is
 * undefined so the global default applies.
 */
function subsidiaryOverride(subsidiaryId?: string): { headers?: Record<string, string> } {
  return subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {};
}

/** Backend hard limit on products-list page size (PaginationParams `le=100`);
 *  larger values 422. */
const MAX_PAGE_SIZE = 100;

/** Convert offset/limit paging into the backend's 1-based `{ page, size }` block.
 *  `size` is clamped to the backend's `le=100` ceiling so an oversized `limit`
 *  can never reach the API (defence-in-depth behind the capped UI options). */
export function toPagination(offset = 0, limit = 100): { page: number; size: number } {
  const size = Math.min(Math.max(Math.trunc(limit) || 1, 1), MAX_PAGE_SIZE);
  return { page: Math.floor(offset / size) + 1, size };
}

/** Fetches a connection's paginated, filtered product catalog. */
export async function listCatalogProducts(
  params: ProductsListRequest,
  subsidiaryId?: string,
): Promise<ProductsListResponse> {
  const {
    connectionId,
    offset = 0,
    limit = 100,
    syncId,
    ingestionDate,
    sortBy,
    filters,
    search,
  } = params;
  return integrationsClient
    .post(endpoints.products.list(connectionId), {
      json: {
        sync_id: syncId ?? null,
        ingestion_date: ingestionDate ?? null,
        pagination: toPagination(offset, limit),
        sort_by: sortBy ?? 'score_desc',
        filters: filters ?? {},
        search: search ?? null,
      },
      ...subsidiaryOverride(subsidiaryId),
    })
    .json<ProductsListResponse>();
}

/** Fetches the identifier fields the catalog search can target for a connector. */
export async function getProductSearchFields(
  connectorCode: string,
  subsidiaryId?: string,
): Promise<ProductSearchFieldsResponse> {
  return integrationsClient
    .get(endpoints.products.searchFields(connectorCode), { ...subsidiaryOverride(subsidiaryId) })
    .json<ProductSearchFieldsResponse>();
}

/** Fetches full connector-shaped detail for a single product. */
export async function getProductDetail(
  connectionId: string,
  externalId: string,
  subsidiaryId?: string,
): Promise<ProductDetailResponse> {
  return integrationsClient
    .get(endpoints.products.detail(connectionId, externalId), {
      ...subsidiaryOverride(subsidiaryId),
    })
    .json<ProductDetailResponse>();
}

/** Fetches the ingestion revisions (syncs) for a connection within a year window. */
export async function listProductRevisions(
  params: ProductRevisionsRequest,
  subsidiaryId?: string,
): Promise<ProductRevisionsResponse> {
  const { connectionId, startDate, endDate, page = 1, size = 50 } = params;
  // The endpoint requires both bounds; default to a rolling one-year window as
  // bare calendar dates (YYYY-MM-DD) when the caller doesn't specify them.
  return integrationsClient
    .post(endpoints.products.syncs(), {
      json: {
        connection_id: connectionId,
        start_date: startDate ?? isoDateDaysAgo(365),
        end_date: endDate ?? todayIsoDate(),
        pagination: { page, size },
      },
      ...subsidiaryOverride(subsidiaryId),
    })
    .json<ProductRevisionsResponse>();
}

/** Fetches the dynamic filter catalog (vendor options etc.) for a connection. */
export async function getProductFilterCatalog(
  params: ProductFiltersRequest,
  subsidiaryId?: string,
): Promise<ProductFiltersResponse> {
  const { connectorCode, connectionId, syncId, ingestionDate, appliedFilters } = params;
  return integrationsClient
    .post(endpoints.products.filters(), {
      json: {
        connector_code: connectorCode,
        connection_id: connectionId,
        sync_id: syncId ?? null,
        ingestion_date: ingestionDate ?? null,
        applied_filters: appliedFilters ?? {},
      },
      ...subsidiaryOverride(subsidiaryId),
    })
    .json<ProductFiltersResponse>();
}

/** Fetches the connector column metadata (labels, groups, sort/filter flags). */
export async function getProductColumns(
  connectorCode: string,
  subsidiaryId?: string,
): Promise<ProductColumnsResponse> {
  return integrationsClient
    .get(endpoints.products.columns(connectorCode), {
      ...subsidiaryOverride(subsidiaryId),
    })
    .json<ProductColumnsResponse>();
}

/** Fetches the connector's default grouping-method options. */
export async function getDefaultGroupingConfig(
  connectorCode: string,
  subsidiaryId?: string,
): Promise<DefaultGroupingConfigResponse> {
  return integrationsClient
    .get(endpoints.products.defaultGroupingConfig(connectorCode), {
      ...subsidiaryOverride(subsidiaryId),
    })
    .json<DefaultGroupingConfigResponse>();
}

/** Fetches the write capabilities (update/delete) a connection supports. */
export async function getSyncCapabilities(
  connectionId: string,
  subsidiaryId?: string,
): Promise<SyncCapabilitiesResponse> {
  return integrationsClient
    .post(endpoints.sync.capabilities(), {
      json: { connection_id: connectionId },
      ...subsidiaryOverride(subsidiaryId),
    })
    .json<SyncCapabilitiesResponse>();
}

/** Pushes a single product update back to the source connection. */
export async function updateSyncProduct(
  params: SyncProductRequest,
  subsidiaryId?: string,
): Promise<SyncProductResponse> {
  return integrationsClient
    .post(endpoints.sync.productsUpdate(), {
      json: { connection_id: params.connectionId, product: params.product },
      ...subsidiaryOverride(subsidiaryId),
    })
    .json<SyncProductResponse>();
}

/** Deletes a single product from the source connection by external id. */
export async function deleteSyncProduct(
  params: SyncProductDeleteRequest,
  subsidiaryId?: string,
): Promise<SyncProductResponse> {
  return integrationsClient
    .post(endpoints.sync.productsDelete(), {
      json: { connection_id: params.connectionId, external_id: params.externalId },
      ...subsidiaryOverride(subsidiaryId),
    })
    .json<SyncProductResponse>();
}

/** Fetches the paginated category breakdown for a connection. */
export async function listCategories(
  params: CategoriesListRequest,
  subsidiaryId?: string,
): Promise<CategoriesListResponse> {
  const { connectionId, offset, limit, syncId, ingestionDate, sortBy, filters } = params;
  return integrationsClient
    .post(endpoints.products.categories(connectionId), {
      json: {
        sync_id: syncId ?? null,
        ingestion_date: ingestionDate ?? null,
        pagination: toPagination(offset, limit),
        sort_by: sortBy,
        filters: filters ?? {},
      },
      ...subsidiaryOverride(subsidiaryId),
    })
    .json<CategoriesListResponse>();
}

/** Fetches the paginated item-group summaries for a connection. */
export async function listItemGroups(
  params: ItemGroupsListRequest,
  subsidiaryId?: string,
): Promise<ItemGroupsListResponse> {
  const { connectionId, offset, limit, matchBy, syncId, ingestionDate, sortBy, filters } = params;
  return integrationsClient
    .post(endpoints.products.variants(connectionId), {
      json: {
        match_by: matchBy ?? 'ITEM_GROUP_ID',
        sync_id: syncId ?? null,
        ingestion_date: ingestionDate ?? null,
        pagination: toPagination(offset, limit),
        sort_by: sortBy,
        filters: filters ?? {},
      },
      ...subsidiaryOverride(subsidiaryId),
    })
    .json<ItemGroupsListResponse>();
}

/** Fetches the parent + child variants for a single item group. */
export async function getItemGroupChildren(
  params: ItemGroupChildrenRequest,
  subsidiaryId?: string,
): Promise<ItemGroupChildrenResponse> {
  const { connectionId, itemGroupId, offset, limit, matchBy, syncId, ingestionDate } = params;
  return integrationsClient
    .post(endpoints.products.variantDetail(connectionId, itemGroupId), {
      json: {
        match_by: matchBy ?? 'ITEM_GROUP_ID',
        sync_id: syncId ?? null,
        ingestion_date: ingestionDate ?? null,
        pagination: toPagination(offset, limit),
      },
      ...subsidiaryOverride(subsidiaryId),
    })
    .json<ItemGroupChildrenResponse>();
}
